// Client-side prediction for the player's own hero: a real core Game, seeded with the match roster and
// seed exactly like offline's, used ONLY to answer "where is my hero and what is it doing RIGHT NOW" —
// never read for anyone else's position, never for hp/damage/kills (see below), never rendered directly
// (net/remoteGame.ts blends its output in through reconciliation, so a correct prediction never visibly
// snaps).
//
// The algorithm is the standard rewind-and-replay: on every snapshot, overwrite the shadow hero with the
// authoritative state the server just confirmed (resync), then re-apply every command the server has not
// acked yet, each at its own tick, on top of the latest standing order (a walk/attack-move/follow) the
// server HAS acked (see commandChannel.ts's own header on why that second part is necessary), stepping the shadow
// Game forward one real tick at a time until it reaches "now" (the client's own current tick, running
// ahead of the server by net/clockSync.ts's lead).
//
// restoreResyncPose() below is the one place this must be airtight: it has to leave the shadow hero in a
// state core's own step() can safely advance from, every single call, not just a plausible-looking subset
// of it — a hero whose `state` says 'dash' but whose `dash` object is stale or missing crashes the instant
// skills.ts's updateSkill() dereferences it (`dash.left`), and the same is true of every other transient
// ability object (`hop`, `channel`, `cast`). protocol.ts's HeroSnapshot carries `stateT` plus enough of each
// object (`dash`/`charge`/`hop`/`castFor`/`channel`) to reconstruct every state this roster actually uses.
// A FEW fields genuinely are fabricated placeholders, because nothing here ever reads them back at all
// (`cast.fn` is a closure and gets a safe no-op; `dash.mult`/`from` only feed a `g.damage()`/fx call inside
// this throwaway shadow) — but that is not true of every field a wire object leaves out, and a review
// caught this module once claiming it was: `dash.hit` (whether Выпад/lunge has connected yet) IS read back,
// by skills.ts's own chain-reset check, to decide whether to reset that cast's cooldown — a fabricated
// empty set told the shadow no hit had landed even when the server's had, silently keeping a real chain
// combo locally uncastable for a network round trip. It travels on the wire now (a boolean; protocol.ts's
// own comment says why nothing finer is needed). Restoring a state this module still cannot reconstruct
// (one this roster doesn't use, or a report missing its own required object) is worse than not restoring
// it: `interrupt()` (below) is what makes "fall back to normal" safe and cheap to reach for instead of
// guessing.
//
// predict() also bounds how many ticks it will ever replay in one call (MAX_REPLAY_TICKS) — without that, a
// real network outage (no snapshot arriving to advance resyncTick) turns every subsequent frame into a
// replay of the entire outage-so-far, growing without bound for as long as the connection stays down.
import * as THREE from 'three';
import { Game, MOBILE_STATES, SKILLS, STEP_DT, type Hero, type HeroState, type RosterEntry, type Zone } from '../../core/game.js';
import { LAYOUT, buildNav, type Layout } from '../../core/layout.js';
import { setTeamBuffs, type MonsterState, type TeamBuffs } from '../../core/monsters.js';
import { scheduleShadowSwap } from '../../core/skills.js';
import { SUMMONS } from '../../core/summons.js';
import type { AnimEvent } from '../../core/events.js';
import type { HeroId } from '../../core/events.js';
import { ARTIFACTS, type ArtifactSlot } from '../../core/artifacts.js';
import { PASSIVES } from '../../core/passives.js';
import type { RelicHold } from '../../core/relics.js';
import type { Team } from '../../core/layout.js';
import { wireRelics, wireSeconds } from './wireSanity.js';
import type { MatchRosterEntry, SnapshotMessage, SummonSnapshot, TimedCommand, TotemSnapshot, YouEffects } from '../../shared/protocol.js';

export type PredictedPose = {
  pos: { x: number; z: number };
  yaw: number;
  lift: number;
  state: HeroState;
  /** On the shadow's clock (its tick count × STEP_DT), like every other time it reports. */
  swingAt: number;
  /** Seconds left, as predicted for `toTick`. */
  cd: { attack: number; Q: number; W: number };
  /** Core's own "is this hero currently walking a path" gate — see gameView.ts's LocalGameView.movingOf,
      duplicated here rather than imported since it reads private-ish Hero fields core does not expose a
      method for; kept in exact lockstep with that one (see this file's own test). */
  moving: boolean;
};

export type Prediction = {
  /** The pose at `toTick`. */
  pose: PredictedPose;
  /** Position at `toTick - 1` — the other end of the tick the renderer interpolates across. */
  prevPos: { x: number; z: number };
  /** How far this call moved the prediction for a tick the previous call had already predicted (new minus
      old, at the newest tick both covered): nonzero only when a snapshot corrected the replay's start or
      an order's ack moved it from its own tick to the server's. Null when there is nothing to compare. The
      renderer absorbs it into a decaying visual offset, so a correction never shows as a one-frame jump. */
  correction: { x: number; z: number; yaw: number } | null;
  animEvents: AnimEvent[];
};

/** States restored via `dash` (dir/left is all their own position math ever reads — see protocol.ts's own
    comment on why `hit`/`mult`/`from` don't need to travel). Covers rhino's windup->dash AND death's
    lungeWindup->lunge: both set `h.dash` from the very first tick of the WINDUP, not just once the dash
    itself starts, so the wire's `dash` field is already populated throughout. */
const DASH_STATES = new Set<HeroState>(['dash', 'windup', 'lunge', 'lungeWindup']);

/** States that need nothing beyond `state`+`stateT` (both now on the wire) to step safely. */
const TIMED_STATES = new Set<HeroState>(['brace', 'recover']);

/* One NavGrid per layout, built on first use: the active map is known only once a match starts. */
let nav: { layout: Layout; grid: ReturnType<typeof buildNav> } | null = null;
function activeNav() {
  if (nav?.layout !== LAYOUT) nav = { layout: LAYOUT, grid: buildNav() };
  return nav.grid;
}
/** How many ticks of predicted position history predict() keeps, for resync()'s own accuracy measurement
    below — comfortably more than a resync is ever stale by (the 20 Hz snapshot cadence plus a slow
    connection's worth of latency), so a lookup essentially never misses once the match is a second old. */
const HISTORY_TICKS = 180;
/** Caps how many ticks predict() will ever replay in one call — see predict()'s own comment: without this,
    a network outage (no snapshot arriving to move resyncTick forward) turns every subsequent frame into a
    replay of the ENTIRE outage so far, growing without bound for as long as the connection stays down —
    measured at 24.5ms/frame (the whole frame budget, before rendering) after a 60s outage on an M-series
    Mac. 180 ticks (3s) is comfortably past what commandChannel.ts's own unacked buffer or a real jitter
    spike ever needs, while still bounding the worst case to a few ms even on a slow device. */
const MAX_REPLAY_TICKS = 180;

export class PredictedGame {
  private readonly game: Game;
  private readonly yourHero: HeroId;
  private resyncTick = 0;
  /** The newest tick a snapshot reported this hero DEAD on, or -1. core's own kill() throws away the
      hero's order, path and every pending action, so any standing order issued before that moment is gone
      server-side — replaying it after the respawn would walk a freshly spawned hero back toward whatever
      it was doing when it died, which the server has no intention of doing. */
  private lastDeadTick = -1;
  private resyncPose: {
    pos: { x: number; z: number }; yaw: number; hp: number; alive: boolean; state: HeroState; stateT: number;
    stunFor: number; cdQ: number; cdW: number; respawnAt: number; moving: boolean;
    dash: { dir: { x: number; z: number }; left: number; hit: boolean } | null;
    charge: { dir: { x: number; z: number }; k: number } | null;
    hop: { from: { x: number; z: number }; to: { x: number; z: number }; dur: number; arc?: number } | null;
    castFor: number | null;
    channel: { dir: { x: number; z: number }; remaining: number } | null;
    artifacts: { E: ArtifactSlot | null; R: ArtifactSlot | null };
    effects: YouEffects;
    atk: SnapshotMessage['you']['atk'] | null;
    eyeFor: number;
    wolfFor: number;
  } | null = null;
  /** Every other hero's pos/alive/hp as last seen — restored on every predict(), see restoreOthers(). */
  private readonly others = new Map<number, { pos: { x: number; z: number }; alive: boolean; hp: number; silhouette: boolean }>();
  /** The totems this resync's snapshot could see — restored on every predict(), see restoreTotems(). */
  private resyncTotems: TotemSnapshot[] = [];
  /** The summons this resync's snapshot could see — puppets restored on every predict(), see restoreSummons(). */
  private resyncSummons: SummonSnapshot[] = [];
  /** The monsters this resync's snapshot could see. Any other shadow monster is restored dead — see
      restoreMonsters(). */
  private resyncMonsters: { id: number; pos: { x: number; z: number }; yaw: number; alive: boolean; hp: number; state: MonsterState; stateT: number }[] = [];
  /** Seconds left per team buff as of the resync — re-applied on every restore (see restoreResyncPose). */
  private resyncBuffs: TeamBuffs | null = null;
  /** The server's match clock/score as of the resync — re-applied on every restore, so the shadow can only
      ever end its match when the real one does (see restoreResyncPose). */
  private resyncMatch: { score: { a: number; b: number }; clock: number; overtime: boolean } | null = null;
  /** Both teams' relics as of the resync (Рог Дикой Охоты changes how fast the own hero walks and swings);
      only the own team's shared cooldown is known, the rival's reads 0 and is never used. */
  private resyncRelics: Record<Team, RelicHold | null> = { a: null, b: null };
  /** Ticks whose anim events this module has already handed out — see predict()'s own comment on why
      re-simulating the same tick twice must never re-report the same event twice. */
  private reportedThroughTick = 0;
  /** tick -> predicted {x,z}, recorded as predict() steps through each tick, purely so the NEXT resync can
      compare what was predicted for a tick against what the server actually reported for it (see
      lastResyncErrorM) — a real accuracy measurement using information this module never had before the
      comparison, unlike diffing the render smoother's own output against itself. */
  private readonly history = new Map<number, { x: number; z: number; yaw: number }>();
  /** The `toTick` of the previous predict() call — see Prediction.correction. */
  private lastToTick = -1;
  /** How far the last resync's authoritative position was from what this module had predicted for that
      same tick, or `null` when there was nothing buffered yet to compare against (the very first snapshot,
      or a resync older than HISTORY_TICKS ever needed to reach). Read by net/remoteGame.ts's applySnapshot
      to drive the `?net` overlay's real prediction-error figure. */
  lastResyncErrorM: number | null = null;

  constructor(roster: MatchRosterEntry[], yourHero: HeroId, seed: number) {
    // `player: true` on the shadow hero at yourHero's own index matters: core only skips bot AI
    // (`bot: player ? null : {...}`, game.ts's spawnHero) for a hero flagged `player`. Marking every
    // shadow hero `player: false` (as an earlier version of this file did) let core's own bot AI drive
    // the player's own shadow hero around during replay whenever no command was in flight — a real,
    // reproducible bug (own-hero "moving" reads true with zero commands issued), not the accepted
    // approximation the module header describes for the OTHER three heroes.
    const coreRoster: RosterEntry[] = roster.map((r, i) => ({ kind: r.hero, team: r.team, player: i === yourHero }));
    // `monsterAi: false`: shadow monsters are puppets placed by resync() — no AI, and damage never lands,
    // so replaying an attack on one can never kill it or grant a buff the server didn't.
    this.game = new Game(activeNav(), coreRoster, { seed, monsterAi: false });
    this.yourHero = yourHero;
    // The OTHER three heroes are puppets too: `bot: null` for each, exactly like the monsters. Left with
    // core's bot AI (as this file did for a long time, calling it an accepted approximation) they cast
    // real skills at the player's own shadow hero — a Крюк pull, a Таран knock, a stun — and every predict()
    // steps the shadow 6–12 ticks per rendered frame, so their cooldowns run that many times faster than
    // real time. The own hero's pose is restored from the wire on the next call, so each such cast was a
    // one-frame excursion of up to ~1.4 m of the RENDERED hero (net/remoteGame.ts snaps past 1.2 m),
    // whenever an enemy stood in range. resync() still places their pos/alive every snapshot.
    for (const h of this.game.heroes) if (h.id !== yourHero) h.bot = null;
  }

  /** Records what the server just confirmed, for every predict() to restore the shadow from: the own hero
      in full, every other hero's `pos`/`alive`/`hp` (see this method's own trailing comment on why), the
      monsters, totems, buffs, relics and match clock. The other heroes' state and cooldowns are never
      read: they are puppets (no bot AI — see the constructor). */
  resync(msg: SnapshotMessage) {
    const hs = msg.heroes[this.yourHero];
    if (!hs || !hs.visible) return;  // your own hero should never be fog-hidden to you; be defensive anyway

    const predicted = this.history.get(msg.tick);
    this.lastResyncErrorM = predicted ? Math.hypot(predicted.x - hs.pos.x, predicted.z - hs.pos.z) : null;
    // Anything at or before this resync's own tick can never be looked up again (every future resync's
    // tick can only move forward) — bounded cleanup, not just a size cap, so a long-idle tab's map doesn't
    // grow across an entire match.
    for (const tick of this.history.keys()) if (tick <= msg.tick) this.history.delete(tick);

    this.resyncTick = msg.tick;
    if (!hs.alive) this.lastDeadTick = msg.tick;
    this.resyncPose = {
      pos: { x: hs.pos.x, z: hs.pos.z }, yaw: hs.yaw, hp: hs.hp, alive: hs.alive, state: hs.state as HeroState,
      stateT: hs.stateT ?? 0, stunFor: hs.stunFor, cdQ: msg.you.cdQ, cdW: msg.you.cdW, moving: hs.moving,
      respawnAt: hs.respawnIn !== undefined ? msg.tick * STEP_DT + hs.respawnIn : 0,
      dash: hs.dash ? { dir: { x: hs.dash.dir.x, z: hs.dash.dir.z }, left: hs.dash.left, hit: !!hs.dash.hit } : null,
      charge: hs.charge ? { dir: { x: hs.charge.dir.x, z: hs.charge.dir.z }, k: hs.charge.k } : null,
      // `'from' in hs.hop` on top of the truthiness check: never trust a declared wire TYPE over what
      // actually arrived — a server still sending the OLDER `{to}`-only shape must degrade to "not enough
      // data" here, the same as a genuinely absent `hop`, not read `undefined.x` and crash.
      hop: hs.hop && 'from' in hs.hop && 'dur' in hs.hop
        ? { from: { x: hs.hop.from.x, z: hs.hop.from.z }, to: { x: hs.hop.to.x, z: hs.hop.to.z }, dur: hs.hop.dur, arc: hs.hop.arc }
        : null,
      castFor: hs.castFor ?? null,
      channel: hs.channel ? { dir: { x: hs.channel.dir.x, z: hs.channel.dir.z }, remaining: hs.channel.remaining } : null,
      artifacts: {
        E: hs.artifacts.E ? { line: hs.artifacts.E.line, cd: msg.you.artifactCdFor.E } : null,
        R: hs.artifacts.R ? { line: hs.artifacts.R.line, cd: msg.you.artifactCdFor.R } : null,
      },
      // `??`: a pre-protocol-4 `you` has none — replay then simply runs unmodified, as it always used to.
      effects: msg.you.effects ?? { slows: [], haste: [], taunt: null, pull: null, cloakFor: 0 },
      atk: msg.you.atk ?? null,
      eyeFor: wireSeconds(msg.you.eyeFor),
      wolfFor: wireSeconds(hs.wolfFor),
    };
    if (this.reportedThroughTick < this.resyncTick) this.reportedThroughTick = this.resyncTick;

    // Every OTHER hero's pos/alive/hp, from this same snapshot — none of it is ever rendered from here, but
    // updateHero()'s attack-order branch (game.ts) faces, chases and swings at WHICHEVER Hero object the
    // order names: the shadow's own copy. Left where the last replay put it, that copy drifted (pushed
    // aside by the own hero walking through it — up to 1.2 m at a 200 ms RTT) and died of the replay's own
    // swings (hp 0 while the real target stood at 108), and a dead target drops the order outright.
    const ownTeam = this.game.heroes[this.yourHero]!.team;
    for (let i = 0; i < msg.heroes.length; i++) {
      if (i === this.yourHero) continue;
      const ohs = msg.heroes[i]!;
      const oh = this.game.heroes[i];
      if (!oh) continue;
      // The server's own verdict on whether this hero is visible — including a Звездочёт reveal the
      // shadow cannot reconstruct — held for the whole replay window, so an attack order on a revealed
      // enemy in fog is not dropped by the shadow's targetVisibleTo() while the server keeps chasing.
      oh.revealedTo[ownTeam] = ohs.visible ? (msg.tick + MAX_REPLAY_TICKS + 1) * STEP_DT : 0;
      if (!ohs.visible) continue;
      this.others.set(i, { pos: { x: ohs.pos.x, z: ohs.pos.z }, alive: ohs.alive, hp: ohs.hp, silhouette: ohs.silhouette === true });
    }
    // Attacking a totem is an order on its id: without the server's totems in the shadow it was dropped.
    this.resyncTotems = msg.totems ?? [];
    // Attacking a summon is an order on its id too, and walking to one needs where it stands.
    this.resyncSummons = (msg.summons ?? []).filter(s => Object.hasOwn(SUMMONS, s.kind) && this.game.heroes[s.owner]);

    // Monsters matter to the replay the same way: an attack order walks to and faces one, and a live monster's
    // body pushes the own hero aside (core's separate()). Restored on every predict(), like the hero itself.
    this.resyncMonsters = (msg.monsters ?? []).filter(ms => ms.visible).map(ms => ({
      id: ms.id, pos: { x: ms.pos.x, z: ms.pos.z }, yaw: ms.yaw, alive: ms.alive, hp: ms.hp, state: ms.state, stateT: ms.stateT,
    }));
    this.resyncBuffs = msg.teamBuffs ?? null;
    this.resyncMatch = { score: { a: msg.score.a, b: msg.score.b }, clock: msg.clock, overtime: msg.overtime };
    const relics = wireRelics(msg.relics), cd = wireSeconds(msg.you.relicCd);
    const hold = (team: Team): RelicHold | null => (relics[team] ? { line: relics[team]!, cd: team === ownTeam ? cd : 0 } : null);
    this.resyncRelics = { a: hold('a'), b: hold('b') };
  }

  /** Replays forward from the last resync to `toTick` (the client's own current tick), applying the
      standing order first (the latest one the server has acked — see commandChannel.ts) and then every
      unacked command at its own tick, and returns the resulting pose plus any of the player's own
      windup/dash/cast anim events that occurred on a tick this call has not already reported (so a cast
      started three frames ago does not re-trigger its windup clip on every one of those three frames' worth
      of re-simulation). Never predicts if there is nothing to resync from yet (before the first snapshot),
      or if the span since the last resync exceeds MAX_REPLAY_TICKS: with no snapshot arriving to move
      resyncTick forward (a real network outage, not ordinary jitter), toTick keeps climbing at 60/s while
      resyncTick sits frozen, so an unbounded replay here re-simulates the ENTIRE outage-so-far on EVERY
      SINGLE FRAME for as long as it lasts — a connection that recovers after the loop grows past this cap
      would otherwise have looked, and cost, worse the longer it stayed down. Past the cap this returns
      null instead, same as "nothing to resync from yet": the caller (remoteGame.ts's reconcile) already
      treats that as "hold at the last rendered pose", which is the honest answer once there is nothing new
      to predict from — continuing to replay commands as if they had effect this far into an outage would
      be fabricating motion, not predicting it. */
  predict(standingOrder: TimedCommand | null, unacked: readonly TimedCommand[], toTick: number): Prediction | null {
    if (!this.resyncPose || toTick - this.resyncTick > MAX_REPLAY_TICKS) return null;
    const h = this.restoreResyncPose();
    const commonTick = Math.min(this.lastToTick, toTick);
    const before = commonTick > this.resyncTick ? this.history.get(commonTick) : undefined;
    this.lastToTick = toTick;
    let prevPos = { x: h.pos.x, z: h.pos.z };

    if (standingOrder && this.standingOrderStillLive(standingOrder)) this.game.apply(this.yourHero, standingOrder.cmd);
    const animEvents: AnimEvent[] = [];
    let i = 0;
    for (let tick = this.resyncTick; tick < toTick; tick++) {
      // Cleared before THIS tick's own apply() calls, not after: castSkill emits its windup anim event
      // synchronously from inside apply() (e.g. rhino's Q: `g.emitAnim(...)` fires the instant the cast is
      // applied, not on some later tick when a windup timer elapses) — clearing afterward would wipe it
      // before it was ever collected below.
      this.game.events.length = 0;
      while (i < unacked.length && unacked[i]!.tick <= tick) { this.game.apply(this.yourHero, unacked[i]!.cmd); i++; }
      prevPos = { x: h.pos.x, z: h.pos.z };
      this.game.step(STEP_DT);
      this.history.set(tick + 1, { x: h.pos.x, z: h.pos.z, yaw: h.yaw });
      if (this.history.size > HISTORY_TICKS) for (const t of this.history.keys()) { this.history.delete(t); if (this.history.size <= HISTORY_TICKS) break; }
      if (tick >= this.reportedThroughTick) for (const e of this.game.events) if (e.type === 'anim' && e.hero === this.yourHero) animEvents.push(e);
    }
    while (i < unacked.length) { this.game.apply(this.yourHero, unacked[i]!.cmd); i++; }
    if (toTick > this.reportedThroughTick) this.reportedThroughTick = toTick;
    const after = before && this.history.get(commonTick);

    return {
      pose: {
        pos: { x: h.pos.x, z: h.pos.z }, yaw: h.yaw, lift: h.lift, state: h.state, swingAt: h.swingAt,
        cd: { attack: h.cd.attack, Q: h.cd.Q, W: h.cd.W },
        moving: h.alive && h.path.length > 0 && !h.pending && MOBILE_STATES.has(h.state),
      },
      prevPos,
      correction: before && after ? { x: after.x - before.x, z: after.z - before.z, yaw: after.yaw - before.yaw } : null,
      animEvents,
    };
  }

  /** A `move` order the hero has already WALKED OUT is finished, and replaying it fabricates a fresh path
      to a point the hero is already standing on: core then re-aims `wantYaw` down that degenerate,
      floating-point-noisy direction every frame, so a hero standing still visibly shivers left and right
      (reported twice from live play) and `moving` flickers under the animation. The server's own report is
      what settles it: with the order's tick already behind the resync and the acked state saying this hero
      has no path left, there is nothing left to predict — re-applying it is invention, not prediction.
      An order the server has NOT acted on yet (tick ahead of the resync) is still replayed, which is the
      whole reason a standing order exists; so is one the server is visibly still walking.

      `attack`/`follow` keep the unconditional replay: standing still is a NORMAL mid-order state for them
      (a hero swinging at something in range has no path either), so "not moving" says nothing about
      whether that order is done. */
  private standingOrderStillLive(standingOrder: TimedCommand): boolean {
    // A death ends every order, whatever its type — see lastDeadTick.
    if (standingOrder.tick <= this.lastDeadTick) return false;
    if (standingOrder.cmd.type !== 'move') return true;
    // `>=`, not `>`: a snapshot for the very tick an order was issued on may have been built before the
    // server applied it, and the two failure modes are not symmetric — treating a live order as finished
    // costs a visible round trip of input lag, treating a finished one as live costs one snapshot of
    // harmless over-prediction.
    return standingOrder.tick >= this.resyncTick || !this.resyncPose || this.resyncPose.moving;
  }

  private restoreResyncPose(): Hero {
    const p = this.resyncPose!;
    this.game.t = this.resyncTick * STEP_DT;
    // The match clock and score are the server's: left to the shadow's own accumulation they ran at the
    // replay-span rate (6–12 ticks per rendered frame, i.e. 6–12x real time) and the shadow reached
    // RULES.win / RULES.matchTime on its own every 1–3 real minutes — core's step() then freezes every
    // hero for 6 s (prediction silently dead for ~0.6 s real) and reset() places them all at base for one
    // predict() call: the rendered own hero snapped to base and back, a camera lurch reported from live
    // play. With these restored the shadow can only ever finish when the real match does, at which point
    // nothing is predicted any more anyway. `winner` cleared unconditionally: a finish() the shadow already
    // reached in an EARLIER call must not keep step() returning early now.
    this.game.winner = null;
    if (this.resyncMatch) {
      this.game.score = { a: this.resyncMatch.score.a, b: this.resyncMatch.score.b };
      this.game.clock = this.resyncMatch.clock;
      this.game.overtime = this.resyncMatch.overtime;
    }
    // Every restore, not once per resync: a buff running out mid-replay nulls it in the shadow, and the next
    // predict() from the same resync must start with it again. Before `h.hp` below — applyBoon() adjusts hp.
    if (this.resyncBuffs) setTeamBuffs(this.game, this.resyncBuffs);
    const r = this.resyncRelics;
    this.game.relics = { a: r.a && { ...r.a }, b: r.b && { ...r.b } };
    const h = this.game.heroes[this.yourHero]!;
    // Око Бестиария speeds the own team's cooldowns and shows it everything; the rival's never matters here.
    this.game.eyeUntil = { a: 0, b: 0 };
    this.game.eyeUntil[h.team] = this.game.t + p.eyeFor;
    // Whatever the previous replay left in flight belongs to a future this call is about to re-simulate:
    // a timer (a delayed swap, a cast's follow-up), a bolt or lob, a zone, a trap. Kept, each fired again
    // on top of the replay's own copy of it, or long after the server's version was over.
    this.game.timers = [];
    this.game.projectiles = []; this.game.lobs = []; this.game.pools = []; this.game.traps = [];
    this.restoreMonsters();
    this.restoreOthers();
    this.restoreTotems();
    this.restoreSummons();
    // Reset EVERY transient ability field to a known-safe baseline first, unconditionally, before anything
    // below tries to re-enter one — game.ts's own `interrupt()` is exactly "cancel whatever ability this
    // hero is mid-way through", already relied on elsewhere (a stun, a knockback) for the same reason.
    // Without this, a field `interrupt()` doesn't cover as a byproduct of some EARLIER predict() call's own
    // local simulation (the shadow completing a dash and nulling `h.dash` itself, say) would otherwise
    // survive into THIS call untouched, waiting to crash the moment `state` gets set back to a value that
    // expects it non-null.
    this.game.interrupt(h);
    h.order = null; h.path = [];
    h.pending = null; h.pendingTotem = null;
    // Not restored FROM anything (the wire only says "has this dash hit yet", not when) — reset instead,
    // so a chain-reset the shadow works out below is never blocked by a stale `chainAt` an EARLIER predict()
    // call's own local simulation left behind. Harmless to always allow: skills.ts's own chain-reset just
    // sets `h.cd.Q = 0` again on a tick it already reads as 0, and does nothing else with `chainAt` at all.
    h.chainAt = undefined;
    h.pos.set(p.pos.x, 0, p.pos.z);
    h.yaw = h.wantYaw = p.yaw;
    h.hp = p.hp;
    h.alive = p.alive;
    h.respawnAt = p.respawnAt;
    h.stunUntil = p.stunFor > 0 ? this.game.t + p.stunFor : 0;
    h.cd.Q = p.cdQ;
    h.cd.W = p.cdW;
    this.restoreAttack(h, p.atk);
    // What changes how fast the hero walks (slows, haste, Сапоги гонца) or whether it obeys its orders at
    // all (a taunt, Крюк's drag, Плащ) — without these the replay walked a slowed hero at full speed, and
    // every snapshot pulled it back.
    h.artifacts = { E: p.artifacts.E && { ...p.artifacts.E }, R: p.artifacts.R && { ...p.artifacts.R } };
    const t = this.game.t, fx = p.effects;
    h.slows = fx.slows.map(s => ({ pct: s.pct, until: t + s.left }));
    h.buffs = fx.haste.map(b => ({ type: 'haste' as const, val: b.val, until: t + b.left }));
    const taunter = fx.taunt && this.game.heroes[fx.taunt.by];
    h.taunt = fx.taunt && taunter ? { by: taunter, until: t + fx.taunt.left } : null;
    h.pull = fx.pull ? { from: v3(fx.pull.from.x, fx.pull.from.z), to: v3(fx.pull.to.x, fx.pull.to.z), t0: t - fx.pull.elapsed, dur: fx.pull.dur } : null;
    h.cloakUntil = t + fx.cloakFor;
    const forcer = fx.forced && this.game.heroes[fx.forced.by];
    h.forced = fx.forced && forcer ? { kind: fx.forced.kind, by: forcer, until: t + fx.forced.left } : null;
    // Сошка / Без отражения: at the cap the hero stands still for good — -Infinity keeps float error from missing it.
    const stillFor = fx.stillFor ?? 0, stillCap = h.kind === 'shadow' ? PASSIVES.shadow.stillFor : PASSIVES.arquebusier.still;
    h.stillSince = stillFor >= stillCap ? -Infinity : t - stillFor;
    // Сапоги гонца: the shadow never takes damage itself, so "out of combat" must come from the server. At the
    // cap (or absent) the hero is quiet for good — -Infinity keeps `t - (t - quiet)` float error from missing it.
    const quietFor = fx.quietFor ?? Infinity;
    h.lastDamageAt = quietFor >= ARTIFACTS.boots.quiet ? -Infinity : t - quietFor;
    h.kb.set(fx.kb?.x ?? 0, 0, fx.kb?.z ?? 0);
    h.wallShadow = fx.wallShadow ? this.restoreWallShadow(h, fx.wallShadow) : null;
    // Обернуться (a melee reach) and Связка (a bond to a spot).
    h.wolfUntil = p.wolfFor > 0 ? t + p.wolfFor : 0;
    // Связка: its ratchet runs in the replay as on the server. A tie to another hero uses the shadow's own copy of
    // him (restored to the snapshot's spot on every predict), so a pair splits a correction in half as the server
    // does instead of the own hero taking all of it and being pulled back half of it by the next snapshot; any
    // other end is a fixed spot where the server last saw it.
    const tie = fx.tether, mate = tie?.mate !== undefined ? this.game.heroes[tie.mate] : undefined;
    h.tether = tie?.to ? {
      a: h, b: mate && mate !== h ? mate : { pos: v3(tie.to.x, tie.to.z), alive: true }, max: tie.max, until: t + tie.left,
      lastA: h.pos.clone(), lastB: v3(tie.to.x, tie.to.z),
    } : null;

    if (DASH_STATES.has(p.state) && p.dash) {
      h.state = p.state;
      h.stateT = p.stateT;
      // `hit` reconstructs whether the REAL dash has connected yet — skills.ts's own chain-reset check
      // reads `dash.hit.size`, not which enemies are in it, so a Set containing just this hero itself (any
      // Hero satisfies the type; its identity is never compared against a real enemy) carries exactly that
      // one bit of real information across, non-empty if and only if the wire said so. `mult`/`from` still
      // never travel — this hero's own damage-dealing/fx calls inside the shadow are never read back
      // (PredictedPose carries no such field, by construction — see this module's own header), so a neutral
      // multiplier and the resync position are harmless placeholders for those two specifically.
      h.dash = { dir: v3(p.dash.dir.x, p.dash.dir.z), left: p.dash.left, hit: new Set(p.dash.hit ? [h] : []), mult: 1, from: h.pos.clone() };
    } else if (p.state === 'charging' && p.charge) {
      h.state = 'charging';
      h.stateT = p.stateT;
      // The wire sends the fill FRACTION (`k`), not the absolute start time `Charge.t0` core itself keeps —
      // skills.ts's own chargeLevel() computes `k` from `t0` the same way (`(g.t - t0) / maxCharge`), so
      // inverting it here reconstructs `t0` exactly, not an approximation.
      h.charge = { dir: v3(p.charge.dir.x, p.charge.dir.z), t0: this.game.t - p.charge.k * SKILLS.arquebusier.Q.maxCharge };
    } else if ((p.state === 'hop' || p.state === 'leap' || p.state === 'feint' || p.state === 'flock') && p.hop) {
      h.state = p.state;
      h.stateT = p.stateT;
      h.hop = { from: v3(p.hop.from.x, p.hop.from.z), to: v3(p.hop.to.x, p.hop.to.z), dur: p.hop.dur, arc: p.hop.arc };
    } else if (p.state === 'casting' && p.castFor !== null) {
      h.state = 'casting';
      h.stateT = p.stateT;
      // `Cast.fn` is a closure — there is nothing on a wire to reconstruct it FROM. A no-op stands in for
      // it safely: updateSkill()'s 'casting' branch only ever reads `fn` once, to call it at the moment
      // this state ends, and that call's real (server-decided) effect is never read back from this shadow
      // either way — only the STATE TRANSITION and its timing (both driven by `castFor`/`stateT`) matter
      // for what predictedGame.ts is ever asked to answer.
      h.cast = { time: p.castFor, fn: () => {} };
    } else if (p.state === 'channel' && p.channel) {
      h.state = 'channel';
      h.stateT = p.stateT;
      // `tickAt` (core's own next-damage-tick instant) starts at "now" rather than the true value — same
      // reasoning as `dash`'s fabricated `mult`/`from` above: only ever consumed by a `g.damage()` call
      // inside this shadow that nothing reads back.
      h.channel = { dir: v3(p.channel.dir.x, p.channel.dir.z), until: this.game.t + p.channel.remaining, tickAt: this.game.t };
    } else if (TIMED_STATES.has(p.state)) {
      h.state = p.state;
      h.stateT = p.stateT;
      // `recoverFor` (core's own lockout duration) is the one thing here NOT on the wire, and unlike
      // everything else restored above, is not reset by interrupt() either — left alone it would carry
      // over a stale value from an EARLIER predict() call's own unrelated local simulation. Forcing it to
      // `stateT` resolves 'recover' on this very tick instead: a lockout state has nothing else that
      // depends on it, so ending it a little early (never late — it can never look like the shadow held an
      // advantage) is a harmless, bounded approximation. `brace`'s own duration is a known skill constant
      // (SKILLS.rhino.W.duration), so it needs no such fallback — `stateT` alone makes it exact.
      if (p.state === 'recover') h.recoverFor = p.stateT;
    }
    // Anything else (a state this module doesn't recognize, or one missing its own required wire object)
    // stays 'normal', exactly what `interrupt()` already left it at above.
    return h;
  }

  /** The basic attack's rhythm (protocol.ts's `you.atk`): without it every replay started from whatever the
      previous one left — a swing restarted on each call (an arquebusier's 15 swings played as 390 clips at
      a 200 ms RTT), or none predicted at all while the stale cooldown ran. A pre-protocol-15 snapshot has
      none: the swing in flight is then dropped, as it always was. */
  private restoreAttack(h: Hero, atk: SnapshotMessage['you']['atk'] | null) {
    if (!atk) return;
    const t = this.game.t;
    h.cd.attack = atk.cd;
    h.clipFlip = atk.flip;
    h.swingAt = t - atk.swingAgo;
    const pd = atk.pending;
    if (!pd) return;
    if (pd.totem !== undefined) { h.pendingTotem = { at: t + pd.in, totemId: pd.totem }; return; }
    const target = pd.hero !== undefined ? this.game.heroes[pd.hero]
      : pd.summon !== undefined ? this.game.summons.find(s => s.id === pd.summon) : this.game.monsters.find(m => m.id === pd.monster);
    if (target) h.pending = { at: t + pd.in, target };
  }

  /** Тень на стене's pending swap (protocol.ts's `you.effects.wallShadow`): the mark, and the swap onto it
      re-armed for the time the server has left. Without it the shadow either swapped a second time from a
      leftover timer or not at all until the server's did, and the rendered hero jumped out and back. */
  private restoreWallShadow(h: Hero, ws: NonNullable<YouEffects['wallShadow']>): Zone {
    const g = this.game;
    const mark: Zone = { pos: v3(ws.pos.x, ws.pos.z), team: h.team, src: h, r: 0.8, until: g.t + ws.left, life: SKILLS.shadow.W.delay, look: 'shadowMark', tickAt: g.t, hideWithSrc: true };
    g.pools.push(mark);
    scheduleShadowSwap(g, h, mark, ws.left);
    return mark;
  }

  /** Every other hero back to what the server last showed, on every call, not once per resync: a replay
      pushes them aside, damages and stuns them, and the next call must not start from that. */
  private restoreOthers() {
    for (const [id, o] of this.others) {
      const oh = this.game.heroes[id];
      if (!oh) continue;
      this.game.interrupt(oh);
      oh.pos.set(o.pos.x, 0, o.pos.z);
      oh.alive = o.alive; oh.hp = o.hp;
      // Без отражения: the server's verdict held for the replay, so an attack order on her drops here as it does there.
      oh.stillSince = o.silhouette ? -Infinity : this.game.t;
      oh.order = null; oh.path = []; oh.pending = null; oh.pendingTotem = null;
      oh.kb.set(0, 0, 0); oh.stunUntil = 0; oh.pull = null; oh.forced = null;
      oh.respawnAt = Infinity;  // only a snapshot revives one
    }
  }

  /** Puppets like the monsters: in place, with their hit points, but never ticking a heal or a hurt. */
  private restoreTotems() {
    const t = this.game.t;
    this.game.totems = [];
    for (const s of this.resyncTotems) {
      const src = this.game.heroes.find(x => x.team === s.team);
      if (!src) continue;
      this.game.totems.push({ id: s.id, kind: s.kind, pos: v3(s.pos.x, s.pos.z), team: s.team, src, r: s.r, until: t + s.left, hp: s.hp, tickAt: Infinity, ticks: 0 });
    }
  }

  /** Puppets like the monsters (the shadow runs no summon AI, `monsterAi: false`): where the snapshot showed
      them, never expiring or hit mid-replay. */
  private restoreSummons() {
    this.game.summons = this.resyncSummons.map(s => ({
      id: s.id, kind: s.kind, owner: this.game.heroes[s.owner]!, team: s.team, slot: 0, radius: SUMMONS[s.kind].radius,
      pos: v3(s.pos.x, s.pos.z), yaw: s.yaw, hp: s.hp, maxHp: s.maxHp, alive: true, until: Infinity, life: s.life,
      target: null, retargetAt: Infinity, attackReadyAt: Infinity, swingAt: -9, path: [], repathAt: 0, marks: [],
    }));
  }

  /** A monster the snapshot didn't show is restored dead: its last-seen body would otherwise collide with the
      predicted hero wherever it no longer is. `respawnAt = Infinity` for every dead one, so core's own
      respawn timer never revives it mid-replay at the den — only a snapshot showing it alive does. */
  private restoreMonsters() {
    for (const m of this.game.monsters) {
      const r = this.resyncMonsters.find(x => x.id === m.id);
      m.target = null; m.path = [];
      if (!r || !r.alive) { m.alive = false; m.respawnAt = Infinity; continue; }
      m.pos.set(r.pos.x, 0, r.pos.z);
      m.yaw = m.wantYaw = r.yaw;
      m.alive = true; m.respawnAt = 0; m.hp = r.hp; m.state = r.state; m.stateT = r.stateT;
    }
  }
}

function v3(x: number, z: number) {
  return new THREE.Vector3(x, 0, z);
}
